/**
 * UniversalKeyBalanceTile — admin dashboard tile showing Emergent
 * Universal Key burn rate + days remaining + recent budget-exhaustion
 * alerts.
 *
 * Surfaces a one-click "Top up" deep-link so the admin can dodge a
 * mid-package budget exhaustion before it bites.
 */
import { useEffect, useState } from "react";
import {
    Wallet,
    AlertCircle,
    ExternalLink,
    Loader2,
    TrendingUp,
    Clock,
} from "lucide-react";
import { adminUniversalKeyBalance } from "@/lib/api";

const LEVEL_STYLES = {
    ok: {
        badge: "bg-[var(--bl-green-soft)] text-[var(--bl-green)]",
        bar: "bg-[var(--bl-green)]",
        label: "Healthy",
    },
    amber: {
        badge: "bg-[var(--bl-amber-soft)] text-[var(--bl-amber)]",
        bar: "bg-[var(--bl-amber)]",
        label: "Top up soon",
    },
    red: {
        badge: "bg-[var(--bl-rust-soft)] text-[var(--bl-rust)]",
        bar: "bg-[var(--bl-rust)]",
        label: "Top up now",
    },
};

function _fmtUsd(n) {
    if (n == null || Number.isNaN(n)) return "—";
    return `$${Number(n).toFixed(2)}`;
}

export default function UniversalKeyBalanceTile() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await adminUniversalKeyBalance();
                if (alive) setData(r);
            } catch {
                /* hide tile gracefully if endpoint flaps */
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (loading) {
        return (
            <div className="bl-card p-4 flex items-center gap-2 text-xs text-bl-muted">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading Universal Key balance…
            </div>
        );
    }
    if (!data) return null;

    const level = LEVEL_STYLES[data.level] || LEVEL_STYLES.ok;
    const pct = Math.max(0, Math.min(100, data.pct_burnt || 0));
    const daysRemaining = data.days_remaining_at_current_burn;

    return (
        <div className="bl-card p-5 space-y-4" data-testid="universal-key-balance-tile">
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-bl-primary" />
                    <h2 className="font-display font-semibold text-base text-bl tracking-tight">
                        Emergent Universal Key
                    </h2>
                </div>
                <span
                    className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.16em] font-medium ${level.badge}`}
                    data-testid="universal-key-level-badge"
                >
                    {level.label}
                </span>
            </div>

            {/* Spend vs cap bar */}
            <div>
                <div className="flex items-baseline justify-between text-xs mb-1.5">
                    <span className="text-bl-muted">
                        <span className="font-mono font-semibold text-bl text-sm">
                            {_fmtUsd(data.mtd_cost_usd)}
                        </span>
                        {" "}of {_fmtUsd(data.configured_monthly_budget_usd)} budget
                    </span>
                    <span className="font-mono text-bl-muted" data-testid="universal-key-pct">
                        {pct}%
                    </span>
                </div>
                <div className="h-2 bg-bl-bg rounded-full overflow-hidden border border-bl/30">
                    <div
                        className={`h-full transition-all duration-500 ${level.bar}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
            </div>

            {/* Burn-rate + days-remaining stats */}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-1">
                <div className="space-y-0.5">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-bl-muted">
                        <TrendingUp className="h-3 w-3" /> Avg daily
                    </div>
                    <div className="text-base font-display font-semibold text-bl font-mono">
                        {_fmtUsd(data.avg_daily_cost_usd)}
                    </div>
                </div>
                <div className="space-y-0.5">
                    <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] text-bl-muted">
                        <Clock className="h-3 w-3" /> Days left
                    </div>
                    <div
                        className={`text-base font-display font-semibold font-mono ${
                            daysRemaining != null && daysRemaining < 5
                                ? "text-[var(--bl-rust)]"
                                : daysRemaining != null && daysRemaining < 10
                                    ? "text-[var(--bl-amber)]"
                                    : "text-bl"
                        }`}
                        data-testid="universal-key-days-left"
                    >
                        {daysRemaining == null ? "—" : `${daysRemaining}d`}
                    </div>
                </div>
                <div className="space-y-0.5">
                    <div className="text-[10px] uppercase tracking-[0.14em] text-bl-muted">
                        Last 7d
                    </div>
                    <div className="text-base font-display font-semibold text-bl font-mono">
                        {_fmtUsd(data.last_7d_cost_usd)}
                    </div>
                </div>
            </div>

            {/* Recent budget-exhaustion alert (last 24h) */}
            {data.recent_budget_failure && (
                <div
                    className="rounded-lg border-l-4 border-l-[var(--bl-rust)] bg-[var(--bl-rust-soft)] p-3 text-xs"
                    data-testid="universal-key-recent-failure"
                >
                    <div className="flex items-center gap-1.5 font-semibold text-[var(--bl-rust)]">
                        <AlertCircle className="h-3.5 w-3.5" />
                        Budget hit a render in the last 24h
                    </div>
                    <p className="text-bl-muted mt-1 leading-relaxed">
                        Asset <code className="font-mono">{data.recent_budget_failure.asset_type}</code>
                        {" "}failed: {(data.recent_budget_failure.error_message || "").slice(0, 140)}
                    </p>
                </div>
            )}

            {/* Top-up CTA */}
            <div className="flex items-center justify-between gap-3 pt-1 border-t border-bl/30">
                <p className="text-[11px] text-bl-muted leading-relaxed">
                    {data.disclaimer}
                </p>
                <a
                    href={data.topup_url}
                    target="_blank"
                    rel="noreferrer"
                    className={
                        data.level === "ok"
                            ? "bl-btn-ghost text-xs gap-1.5 shrink-0"
                            : "bl-btn-primary text-xs gap-1.5 shrink-0"
                    }
                    data-testid="universal-key-topup-btn"
                >
                    Top up <ExternalLink className="h-3 w-3" />
                </a>
            </div>
        </div>
    );
}
